// src/utils/noteInclusions.js
// r1-3f-util-fetch-2b7d9e14
// Shared helper for the per-invoice note inclusion picker (Seam r1-3f).
// Fetches customer-facing polymorphic notes for a set of jobs, so the picker
// shows only jobs that actually have customer-facing notes. Pairs with the
// buildCustomerNotesData backend contract: batchCreateInvoices accepts
// noteInclusions keyed by job id of shape { includedNoteIds: string[] }.
//
// legnotes9-6b4e8d12 — SEAM 9. This helper used to also report each job's legacy
// job.techNotes[] presence as `hasLegacy`, and emit an includeLegacyTechNotes
// boolean. Seam 8 deleted that array everywhere, so both are gone. The backend
// still ACCEPTS includeLegacyTechNotes (old mobile bundles send it) and ignores it.

import { collection, query, where, getDocs } from 'firebase/firestore';

// Count photo attachments. Mirrors NoteCard's photo predicate
// (m.type === 'photo' || undefined) but for an at-a-glance count only.
const countPhotos = (media) =>
  (Array.isArray(media) ? media : []).filter(
    (m) => m && (m.type === 'photo' || m.type === undefined)
  ).length;

// Short single-line preview of a note's text for a picker row.
const previewText = (text) => {
  const t = typeof text === 'string' ? text.trim().replace(/\s+/g, ' ') : '';
  if (!t) return '(photo only)';
  return t.length > 80 ? `${t.slice(0, 79)}…` : t;
};

// Fetch customer-facing note groups for the given full job docs.
// Returns ONE group per job that HAS customer-facing notes (polymorphic OR
// legacy); jobs with neither are omitted:
//   { jobId, jobLabel, notes: [{ id, preview, photoCount }], hasLegacy }
//
// Query strategy (notes audit): attachedIndex array-contains 'job:{jobId}'
// ONLY, then filter visibility === 'customer' in JS. attachedIndex is a
// single-field array — Firestore auto-indexes it, so NO composite index is
// needed (matches useOrgNotes). Firestore allows one array-contains per query,
// so we issue one query per job (hand-selected batches are small). A failed
// query throws to the caller, which degrades toward including everything.
export const fetchCustomerNoteGroups = async (db, orgId, jobs) => {
  const notesRef = collection(db, `organizations/${orgId}/notes`);
  const groups = [];
  for (const job of jobs) {
    if (!job || !job.id) continue;
    const snap = await getDocs(
      query(notesRef, where('attachedIndex', 'array-contains', `job:${job.id}`))
    );
    const notes = [];
    snap.forEach((docSnap) => {
      const n = docSnap.data();
      if (n.visibility !== 'customer') return; // JS visibility filter
      notes.push({ id: docSnap.id, preview: previewText(n.text), photoCount: countPhotos(n.media) });
    });
    // legnotes9-6b4e8d12 — SEAM 9. `hasLegacy` is gone. It reported whether the job
    // carried a customer-facing job.techNotes[] entry, which drove the picker's
    // "Older job notes from this job" checkbox and the includeLegacyTechNotes flag.
    // Seam 8 deleted that array from every job, so it was permanently false: the
    // checkbox could not render and the flag was always true.
    if (notes.length === 0) continue; // omit jobs with no notes
    groups.push({ jobId: job.id, jobLabel: job.title || job.jobNumber || 'Job', notes });
  }
  return groups;
};

// Build the noteInclusions payload from the picker's exclusion sets. Everything
// is ticked by default, so we track what the user UNticked. A group with nothing
// unticked yields the SAME set the backend builds with no entry — so a
// picker-created invoice matches one created without the picker.
// legnotes9-6b4e8d12 — SEAM 9. The third argument (excludedLegacyJobIds) and the
// emitted includeLegacyTechNotes key are gone. The key's value was
// `g.hasLegacy ? ... : true`, and hasLegacy was permanently false, so it was
// always `true`. The backend gate reads `!== false`, so omitting it entirely is
// identical to sending true.
export const buildNoteInclusions = (groups, excludedNoteIds) => {
  const payload = {};
  (groups || []).forEach((g) => {
    payload[g.jobId] = {
      includedNoteIds: g.notes.map((n) => n.id).filter((id) => !excludedNoteIds.has(id)),
    };
  });
  return payload;
};
